import { ArrowUpDown, ArrowUp, ArrowDown, Package, ExternalLink, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { WatchlistItem, SortColumn, SortDirection } from '../types';
import { getChecklistColor, getTotalQty, openProductPage } from '../utils/helpers';

interface WatchlistTableProps {
  items: WatchlistItem[];
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  onItemClick: (item: WatchlistItem) => void;
  onRemove: (productCode: string) => void;
  allChecklistIds: string[];
}

export function WatchlistTable({
  items,
  sortColumn,
  sortDirection,
  onSort,
  onItemClick,
  onRemove,
  allChecklistIds,
}: WatchlistTableProps) {
  const { t } = useLanguage();

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
    );
  };

  const headerClass = 'px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors';

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className={`${headerClass} w-16`} onClick={() => onSort('qty')}>
                <div className="flex items-center gap-1.5">
                  {t('watchlist.qty', 'Qty')}
                  {renderSortIcon('qty')}
                </div>
              </th>
              <th className={headerClass} onClick={() => onSort('name')}>
                <div className="flex items-center gap-1.5">
                  {t('watchlist.product', 'Product')}
                  {renderSortIcon('name')}
                </div>
              </th>
              <th className={`${headerClass} w-28`} onClick={() => onSort('price')}>
                <div className="flex items-center gap-1.5">
                  {t('watchlist.price', 'Price')}
                  {renderSortIcon('price')}
                </div>
              </th>
              <th className={`${headerClass} w-28`} onClick={() => onSort('status')}>
                <div className="flex items-center gap-1.5">
                  {t('watchlist.status', 'Status')}
                  {renderSortIcon('status')}
                </div>
              </th>
              <th className={`${headerClass} w-32`} onClick={() => onSort('source')}>
                <div className="flex items-center gap-1.5">
                  {t('watchlist.source', 'Source')}
                  {renderSortIcon('source')}
                </div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                {t('watchlist.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item, index) => {
              const totalQty = getTotalQty(item);
              const hasPriceDrop = item.originalPrice != null &&
                item.currentPrice != null &&
                item.currentPrice < item.originalPrice;

              return (
                <tr
                  key={item.id}
                  className={`hover:bg-primary-50 cursor-pointer transition-colors ${
                    index % 2 === 1 ? 'bg-gray-50/50' : ''
                  }`}
                  onClick={() => onItemClick(item)}
                >
                  {/* Qty Column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-sm font-semibold text-gray-700">
                      {totalQty}
                    </span>
                  </td>

                  {/* Product Column */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-12 h-12 object-contain bg-gray-100 rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{item.productCode}</div>
                      </div>
                    </div>
                  </td>

                  {/* Price Column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {item.currentPrice != null ? (
                      <div>
                        <div className={`text-sm font-semibold ${hasPriceDrop ? 'text-green-600' : 'text-gray-900'}`}>
                          ${item.currentPrice.toFixed(2)}
                        </div>
                        {hasPriceDrop && (
                          <div className="text-xs text-gray-400 line-through">
                            ${item.originalPrice?.toFixed(2)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  {/* Status Column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                        item.availability === 'In Stock'
                          ? 'bg-green-100 text-green-800'
                          : item.availability === 'Low Stock'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.availability || t('watchlist.unknown', 'Unknown')}
                    </span>
                  </td>

                  {/* Source Column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {item.checklists && item.checklists.length > 0 ? (
                        <>
                          {item.checklists.slice(0, 2).map(c => (
                            <span
                              key={c.id}
                              className="inline-flex px-2 py-0.5 text-xs font-medium rounded text-white truncate max-w-20"
                              style={{ backgroundColor: getChecklistColor(c.id, allChecklistIds) }}
                              title={c.name}
                            >
                              {c.name}
                              {c.quantity_needed && c.quantity_needed > 1 && (
                                <span className="ml-0.5 opacity-80">x{c.quantity_needed}</span>
                              )}
                            </span>
                          ))}
                          {item.checklists.length > 2 && (
                            <span className="text-xs text-gray-500">+{item.checklists.length - 2}</span>
                          )}
                        </>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-gray-700 text-white">
                          {t('watchlist.manual', 'Manual')}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Actions Column */}
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          openProductPage(item.productCode);
                        }}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title={t('watchlist.viewOnIkea', 'View on IKEA')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onRemove(item.productCode);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('watchlist.remove', 'Remove from watchlist')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
